'use client';

import { motion } from 'framer-motion';
import { LuvImage } from '@/components';
import {
  TypeLink_internalFields,
  TypeMoreToLoveFields,
} from '@/core/generated-types';
import { Asset, Entry } from 'contentful';
import React, { useState } from 'react';
import { UniversalLinkFields } from '@/components/common/UniversalLink';
import classNames from 'classnames';
import Link from 'next/link';
import { slugBuilder } from '@/core/contentfulConfig';

const ImageCard = ({
  image,
  item,
  link,
}: {
  image: Asset;
  item: string;
  link: Entry<UniversalLinkFields>;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const hoverAnimation = {
    hover: {
      scale: 1.04,
    },
    initial: {
      scale: 1,
    },
  };

  const href = slugBuilder(link as Entry<TypeLink_internalFields>);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="w-full"
    >
      <Link href={href}>
        <div className="w-full">
          <motion.div
            variants={hoverAnimation}
            initial="initial"
            animate={isHovered ? 'hover' : 'initial'}
            transition={{ duration: 0.3 }}
            className="w-full"
          >
            <LuvImage
              Image={image}
              altText={image.fields.description}
              className="w-full h-full max-w-[550px]"
            />
          </motion.div>
        </div>
        <p
          className={classNames(
            'text-2xl mt-2 md:text-[32px] md:mt-4 transition-[color] duration-300 md:text-center',
            { 'text-indigo': isHovered },
          )}
        >
          {item}
        </p>
      </Link>
    </div>
  );
};

const MoreToLove = ({
  title,
  firstItem,
  secondItem,
  images,
  ctas,
}: TypeMoreToLoveFields) => {
  const items = [firstItem, secondItem];

  return (
    <div className="bg-white" data-testid="more-to-love">
      <div className="max-w-[1220px] mx-auto px-6 pt-8 pb-10 font-bold md:pt-20 md:pb-24">
        <p className="text-center text-[32px] leading-10 md:text-[42px]">
          {title}
        </p>
        <div className="flex flex-col gap-6 mt-5 md:flex-row md:mt-12 md:justify-center md:gap-5">
          {ctas &&
            images?.map((image, index) => (
              <ImageCard
                key={image.sys.id}
                image={image}
                item={items[index] ?? ''}
                link={ctas[index]}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default MoreToLove;
